/* eslint-disable react-refresh/only-export-components */
// `warningLabel` is imported by InvocationLogDetails.jsx; moving it to a new file is not worth it.
import { Badge, Group, Stack, Text, Tooltip } from "@mantine/core";
import { IconAlertTriangleFilled } from "@tabler/icons-react";

/**
 * Human-readable labels for RunWarningCode. The API serializes the C# enum as camelCase, so
 * every lookup here goes through the lower-cased key (see CLAUDE.md — multi-word enums arrive
 * as "toolCallFailed", not "ToolCallFailed" or "toolcallfailed").
 */
const warningLabels = {
  toolcallfailed: "Tool call failed",
  providerfallback: "Provider fallback",
  providerretry: "Provider retry",
  chainrestarted: "Tool loop restarted",
  iterationlimitreached: "Iteration limit reached",
  responsetruncated: "Response truncated",
  contentblocked: "Content blocked",
  childagentfailed: "Sub-agent failed",
  childagentdegraded: "Sub-agent degraded",
  modelautorouted: "Model auto-routed",
  cancelled: "Cancelled",
  repeatedtoolcall: "Repeated tool call blocked",
  progressstalled: "Progress stalled",
  schemarejected: "Schema rejected",
  vendortoolunavailable: "Vendor tool unavailable",
  leakedtoolcallsastext: "Tool calls leaked as text",
  emptysynthesis: "Empty synthesis discarded",
};

export function warningLabel(code) {
  return warningLabels[(code || "").toLowerCase()] || code || "Warning";
}

/**
 * Run status, plus the part status cannot say.
 *
 * "Succeeded" only ever meant "we returned an answer". A run that called four tools, got a 502
 * from each, fell back to a second model and then hit its iteration ceiling recorded the exact
 * same badge as a run where everything worked. The marker makes that difference visible at a
 * glance and the tooltip says what actually happened.
 */
function StatusBadge({
  status,
  warnings,
  warningCount = 0,
  cancelledAt,
  cancelRequestedAt,
  failureReason,
  size = "sm",
}) {
  // Wire values are camelCased C# enum names, so compare lower-cased (CLAUDE.md).
  const s = (status || "").toLowerCase();
  const count = warningCount || warnings?.length || 0;

  // Four states, not two. This used to be `status !== "succeeded"`, which painted a run that was
  // still executing as a red "pending" — indistinguishable from one that had actually failed.
  //
  // Cancellation is keyed on cancelledAt (the run stopped), never on cancelRequestedAt (somebody
  // asked): a stop landing after a run's last checkpoint marks a run that then finished normally.
  // A request without a stop is its own state — the run has not noticed yet.
  const state = cancelledAt
    ? { label: "cancelled", color: "gray", variant: "light" }
    : s === "pending"
      ? cancelRequestedAt
        ? { label: "stopping", color: "orange", variant: "light" }
        : { label: "running", color: "blue", variant: "light" }
      : s === "succeeded"
        ? // Success is the common case and gets the quiet treatment — a solid green block was the
          // loudest thing on a screen whose whole job is to surface the parts that went wrong.
          { label: status, color: "teal", variant: "light" }
        : // Failure keeps the filled variant, so weight tracks how much the badge is telling you.
          { label: status, color: "red", variant: "filled" };

  const badge = (
    <Badge size={size} variant={state.variant} color={state.color}>
      {state.label}
    </Badge>
  );

  // A failed run's badge explains itself on hover, when the backend recorded a reason
  // (runs from before FailureReason existed show just the badge).
  if (s === "failed" && !cancelledAt && failureReason) {
    return (
      <Tooltip
        withArrow
        multiline
        w={380}
        maw="calc(100vw - 2rem)"
        events={{ hover: true, focus: true, touch: true }}
        label={<Text fz="xs">{failureReason}</Text>}
      >
        {badge}
      </Tooltip>
    );
  }

  // The warning marker rides alongside a badge that keeps saying "succeeded", because it did —
  // the run produced an answer, it just isn't the clean story the word implies. A failed run has
  // nothing to add, and a cancelled one already carries its explanation in the badge itself.
  if (s !== "succeeded" || cancelledAt || count === 0) return badge;

  // Same code repeated (e.g. six failed calls to the same tool) collapses to one line with a
  // multiplier — a run against a dead endpoint would otherwise be an unreadable wall.
  const grouped = [];
  for (const w of warnings || []) {
    const key = `${w.code}|${w.subject || ""}`;
    const existing = grouped.find((g) => g.key === key);
    if (existing) existing.n += 1;
    else grouped.push({ key, n: 1, ...w });
  }

  return (
    <Tooltip
      withArrow
      multiline
      w={380}
      maw="calc(100vw - 2rem)"
      // Touch screens have no hover, so without this the marker is a yellow triangle that
      // explains nothing. Tapping it opens the same summary a mouse gets.
      events={{ hover: true, focus: true, touch: true }}
      label={
        <Stack gap={4}>
          <Text fz="xs" fw={600}>
            Finished, but not cleanly — {count}{" "}
            {count === 1 ? "warning" : "warnings"}
          </Text>
          {grouped.slice(0, 8).map((w) => (
            <Text key={w.key} fz="xs">
              • {warningLabel(w.code)}
              {w.n > 1 ? ` ×${w.n}` : ""}: {w.message}
            </Text>
          ))}
          {grouped.length > 8 && (
            <Text fz="xs" c="dimmed">
              …and {grouped.length - 8} more
            </Text>
          )}
        </Stack>
      }
    >
      <Group gap={4} wrap="nowrap" style={{ display: "inline-flex" }}>
        {badge}
        <IconAlertTriangleFilled
          size={size === "xs" ? 12 : 14}
          color="var(--mantine-color-yellow-6)"
        />
      </Group>
    </Tooltip>
  );
}

export default StatusBadge;
